import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, X } from 'lucide-react';

const PC_CATEGORIES = ['gaming_pc', 'office_pc', 'workstation'];
const PC_DEFAULT_SPEC_KEYS = ['Alaplap', 'CPU', 'CPU hűtő', 'Memória', 'Tárhely', 'Videokártya', 'Tápegység', 'Ház'];

export const SpecsEditor = ({ specs, onChange, label, placeholder, category }) => {
    const [newRows, setNewRows] = useState([]);
    const entries = Object.entries(specs || {});
    const isPC = PC_CATEGORIES.includes(category);

    const addSpec = () => {
        setNewRows(prev => [...prev, { key: '', value: '' }]);
    };

    const addMultipleSpecs = () => {
        setNewRows(prev => [...prev, { key: '', value: '' }, { key: '', value: '' }, { key: '', value: '' }]);
    };

    const prefillDefaults = () => {
        const existing = Object.keys(specs || {}).map(k => k.toLowerCase());
        const newSpecs = { ...specs };
        PC_DEFAULT_SPEC_KEYS.forEach(key => {
            if (!existing.includes(key.toLowerCase())) {
                newSpecs[key] = '';
            }
        });
        onChange(newSpecs);
    };

    const updateSpec = (oldKey, newKey, value) => {
        const newSpecs = { ...specs };
        if (oldKey !== newKey) {
            delete newSpecs[oldKey];
        }
        if (newKey.trim()) {
            newSpecs[newKey] = value;
        }
        onChange(newSpecs);
    };

    const removeSpec = (key) => {
        const newSpecs = { ...specs };
        delete newSpecs[key];
        onChange(newSpecs);
    };

    const updateNewRow = (index, field, value) => {
        setNewRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
    };

    const removeNewRow = (index) => {
        setNewRows(prev => prev.filter((_, i) => i !== index));
    };

    const commitNewRows = () => {
        const newSpecs = { ...specs };
        newRows.forEach(r => {
            if (r.key.trim()) {
                let val = r.value;
                if (!isNaN(val) && val.trim !== undefined && val.trim() !== '') {
                    val = Number(val);
                }
                newSpecs[r.key.trim()] = val;
            }
        });
        onChange(newSpecs);
        setNewRows([]);
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>{label}</Label>
                <div className="flex gap-1.5">
                    {isPC && entries.length < 8 && (
                        <Button type="button" variant="outline" size="sm" onClick={prefillDefaults} data-testid="prefill-pc-specs-btn">
                            <Plus className="w-3 h-3 mr-1" />PC alapértékek
                        </Button>
                    )}
                    <Button type="button" variant="outline" size="sm" onClick={addSpec} data-testid="add-spec-btn">
                        <Plus className="w-3 h-3 mr-1" />Hozzáadás
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={addMultipleSpecs} data-testid="add-multi-spec-btn">
                        <Plus className="w-3 h-3 mr-1" />+3 sor
                    </Button>
                </div>
            </div>
            {entries.length === 0 && newRows.length === 0 && (
                <p className="text-sm text-muted-foreground">{placeholder}</p>
            )}
            {entries.map(([key, value], idx) => (
                <div key={`existing-${idx}`} className="flex gap-2 items-center">
                    <Input 
                        value={key} 
                        onChange={(e) => updateSpec(key, e.target.value, value)}
                        placeholder="Kulcs (pl: CPU)"
                        className="flex-1"
                    />
                    <Input 
                        value={typeof value === 'object' ? JSON.stringify(value) : String(value)} 
                        onChange={(e) => {
                            let val = e.target.value;
                            if (!isNaN(val) && val.trim() !== '') {
                                val = Number(val);
                            }
                            updateSpec(key, key, val);
                        }}
                        placeholder="Érték (pl: Intel i9)"
                        className="flex-1"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeSpec(key)}>
                        <X className="w-4 h-4 text-destructive" />
                    </Button>
                </div>
            ))}
            {newRows.map((row, idx) => (
                <div key={`new-${idx}`} className="flex gap-2 items-center">
                    <Input
                        value={row.key}
                        onChange={(e) => updateNewRow(idx, 'key', e.target.value)}
                        placeholder="Kulcs (pl: CPU)"
                        className="flex-1 border-dashed"
                        autoFocus={idx === newRows.length - 1}
                    />
                    <Input
                        value={row.value}
                        onChange={(e) => updateNewRow(idx, 'value', e.target.value)}
                        placeholder="Érték (pl: Intel i9)"
                        className="flex-1 border-dashed"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeNewRow(idx)}>
                        <X className="w-4 h-4 text-destructive" />
                    </Button>
                </div>
            ))}
            {newRows.length > 0 && (
                <div className="flex gap-2 pt-1">
                    <Button type="button" size="sm" onClick={commitNewRows} className="text-xs" data-testid="commit-new-specs-btn">
                        Sorok alkalmazása ({newRows.filter(r => r.key.trim()).length})
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setNewRows([])} className="text-xs">
                        Mégse
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SpecsEditor;
